import { defineStore } from 'pinia';

export interface CartItem {
  productId: string;
  // The specific marketplace offer this line is for. Different offers of the
  // same product are separate cart lines (different sellers, different prices).
  // Optional for backward-compat with cart entries saved before per-offer
  // purchasing — those continue to dedupe by productId alone.
  offerId?: string;
  merchantName?: string;
  name: string;
  priceCents: number;
  coverUrl: string | null;
  imageUrl: string | null;
  platform: string | null;
}

interface State {
  /** The user the in-memory cart belongs to. null means "no session": the
   * cart is empty and nothing is persisted, no matter what localStorage
   * holds for other users. App.vue calls bindUser() to flip this whenever
   * the auth identity changes. */
  userId: string | null;
  items: CartItem[];
}

// Per-transaction cap surfaced as N/X in the cart UI. Spec calls this the
// "Kinguin API purchase limit per transaction" — exact value depends on the
// merchant tier. 10 is a conservative working value; bump or wire to env when
// the real limit is confirmed. Each cart line is a single key, so this is the
// max number of lines per order.
export const KINGUIN_ORDER_LIMIT = 10;

// One bucket per signed-in user. The key includes the user id so two
// accounts on the same device can't see each other's cart.
const KEY_PREFIX = 'gamzor.cart.v1';

function bucketKey(userId: string | null): string | null {
  return userId ? `${KEY_PREFIX}:${userId}` : null;
}

function lineKey(productId: string, offerId: string | undefined): string {
  return `${productId}::${offerId ?? ''}`;
}

function matches(item: CartItem, productId: string, offerId: string | undefined): boolean {
  if (item.productId !== productId) return false;
  // Treat missing offerId on both sides as equal — legacy entries stay
  // dedup-able by productId alone.
  return (item.offerId ?? null) === (offerId ?? null);
}

function isCartItem(x: unknown): x is CartItem {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.productId === 'string' &&
    typeof o.name === 'string' &&
    typeof o.priceCents === 'number'
  );
}

function readBucket(userId: string | null): CartItem[] {
  const key = bucketKey(userId);
  if (!key) return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isCartItem);
  } catch {
    return [];
  }
}

function writeBucket(userId: string | null, items: CartItem[]) {
  const key = bucketKey(userId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(items));
  } catch {
    // private mode, quota — silently skip; cart will just be in-memory
  }
}

function deleteBucket(userId: string | null) {
  const key = bucketKey(userId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export const useCartStore = defineStore('cart', {
  // Important: do NOT load from storage here. The cart is empty until App.vue
  // binds a user, otherwise stale localStorage from a previous owner would
  // populate before we know who is signed in.
  state: (): State => ({ userId: null, items: [] }),
  getters: {
    count: (s) => s.items.length,
    subtotalCents: (s) => s.items.reduce((n, i) => n + i.priceCents, 0),
    isEmpty: (s) => s.items.length === 0,
    isAtLimit(): boolean {
      return this.count >= KINGUIN_ORDER_LIMIT;
    },
    remainingSlots(): number {
      return Math.max(0, KINGUIN_ORDER_LIMIT - this.count);
    },
  },
  actions: {
    /**
     * Bind the cart to a user (or to nobody on sign-out). Loads that user's
     * bucket from localStorage. Any time the bound identity changes — sign-
     * out, sign-in as someone else, silent token expiry — the previous
     * owner's bucket is deleted, so their cart is "gone" from the device,
     * not just hidden. App.vue is the single caller.
     */
    bindUser(userId: string | null) {
      if (this.userId === userId) return;
      if (this.userId !== null) deleteBucket(this.userId);
      this.userId = userId;
      this.items = readBucket(userId);
    },
    keyOf(item: Pick<CartItem, 'productId' | 'offerId'>): string {
      return lineKey(item.productId, item.offerId);
    },
    /**
     * Add a product+offer line to the cart. Each line is a single key, so
     * adding the same product+offer twice is rejected as a duplicate. Returns
     * the outcome so the caller can show the right message. `at` re-inserts
     * a line where it was (undo of a removal); new lines append.
     */
    add(item: CartItem, at?: number): 'added' | 'duplicate' | 'limit' {
      const existing = this.items.find((i) => matches(i, item.productId, item.offerId));
      if (existing) {
        // Refresh denormalized fields in case price/cover/merchant changed.
        existing.name = item.name;
        existing.priceCents = item.priceCents;
        existing.coverUrl = item.coverUrl;
        existing.imageUrl = item.imageUrl;
        existing.platform = item.platform;
        existing.merchantName = item.merchantName;
        writeBucket(this.userId, this.items);
        return 'duplicate';
      }
      if (this.remainingSlots <= 0) return 'limit';
      this.items.splice(at ?? this.items.length, 0, { ...item });
      writeBucket(this.userId, this.items);
      return 'added';
    },
    remove(productId: string, offerId?: string) {
      this.items = this.items.filter((i) => !matches(i, productId, offerId));
      writeBucket(this.userId, this.items);
    },
    clear() {
      this.items = [];
      // Wipe the bucket so the next reader (or a returning visitor on the
      // same device) can't recover the cart from localStorage.
      deleteBucket(this.userId);
    },
  },
});
